import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9F6F0] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <div className="bg-white rounded-lg shadow-lg p-8 sm:p-12">
          <h1 className="text-4xl font-bold text-[#2C2C2C] mb-4">Politique de Confidentialité</h1>
          <p className="text-sm text-[#5C5C5C] mb-8">Dernière mise à jour : 3 juin 2026</p>

          <div className="space-y-6 text-[#2C2C2C] leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
              <p>
                Bienvenue sur Le Mystère de la Croix. Nous respectons votre vie privée et nous nous engageons à protéger vos données personnelles. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos informations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Données Collectées</h2>
              <p className="mb-2">Nous collectons les informations suivantes lorsque vous achetez notre ebook :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Nom complet</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone mobile (pour le paiement Mobile Money)</li>
                <li>Mode de paiement choisi (Orange Money, M-Pesa, Airtel Money)</li>
                <li>Informations de transaction (montant, date, statut)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. Utilisation des Données</h2>
              <p className="mb-2">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Traiter votre paiement via Bkapay</li>
                <li>Vous envoyer le lien de téléchargement de l'ebook par email</li>
                <li>Gérer votre commande et fournir un support client</li>
                <li>Prévenir la fraude et assurer la sécurité des transactions</li>
                <li>Améliorer nos services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. Partage des Données</h2>
              <p className="mb-2">
                Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations uniquement avec :
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Bkapay</strong> : Notre processeur de paiement pour traiter les transactions Mobile Money</li>
                <li><strong>Fournisseurs de services email</strong> : Pour vous envoyer le lien de téléchargement</li>
                <li><strong>Autorités légales</strong> : Si requis par la loi</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">5. Sécurité des Données</h2>
              <p>
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, perte, destruction ou divulgation. Toutes les transactions sont sécurisées via HTTPS et les données sensibles sont chiffrées.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Conservation des Données</h2>
              <p>
                Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales. Les informations de transaction sont conservées conformément aux exigences comptables et fiscales de la République Démocratique du Congo.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Vos Droits</h2>
              <p className="mb-2">Vous disposez des droits suivants concernant vos données personnelles :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Accès</strong> : Demander une copie de vos données</li>
                <li><strong>Rectification</strong> : Corriger des données inexactes</li>
                <li><strong>Suppression</strong> : Demander la suppression de vos données</li>
                <li><strong>Opposition</strong> : Vous opposer au traitement de vos données</li>
                <li><strong>Portabilité</strong> : Recevoir vos données dans un format structuré</li>
              </ul>
              <p className="mt-3">
                Pour exercer ces droits, contactez-nous à l'adresse email fournie ci-dessous.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Cookies</h2>
              <p>
                Notre site utilise des cookies essentiels pour assurer le bon fonctionnement de la plateforme et améliorer votre expérience. Aucun cookie publicitaire n'est utilisé. Vous pouvez configurer votre navigateur pour refuser les cookies, mais cela peut affecter certaines fonctionnalités du site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Paiements Mobile Money</h2>
              <p>
                Les paiements sont traités par Bkapay, un processeur de paiement sécurisé spécialisé dans les transactions Mobile Money en Afrique. Nous ne stockons pas les détails complets de votre compte Mobile Money. Pour plus d'informations sur la sécurité des paiements, consultez la politique de confidentialité de Bkapay.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Modifications de cette Politique</h2>
              <p>
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Les modifications seront publiées sur cette page avec une date de mise à jour révisée. Nous vous encourageons à consulter régulièrement cette page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Contact</h2>
              <p className="mb-2">
                Pour toute question concernant cette politique de confidentialité ou vos données personnelles, veuillez nous contacter :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mt-3">
                <p><strong>Le Mystère de la Croix</strong></p>
                <p>Email : contact@mysterecroix.com</p>
                <p>Site web : https://ebook-foi-rdc.emergent.host</p>
                <p>Région : République Démocratique du Congo</p>
              </div>
            </section>

            <section className="border-t pt-6 mt-8">
              <p className="text-sm text-[#5C5C5C]">
                En utilisant notre service, vous acceptez les termes de cette politique de confidentialité.
                Si vous n'êtes pas d'accord avec ces termes, veuillez ne pas utiliser notre service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;